import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ClipboardList, Stethoscope, FileText, ArrowRight } from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import StatCard from '../../components/StatCard';
import StatusBadge from '../../components/StatusBadge';
import LoadingSpinner from '../../components/LoadingSpinner';
import EmptyState from '../../components/EmptyState';
import OnboardingProgress from '../../components/OnboardingProgress';
import OnboardingTour from '../../components/OnboardingTour';
import * as vendorApi from '../../api/vendor.api';
import { errorMessage } from '../../api/client';
import { formatDate } from '../../utils/format';
import { useAuth } from '../../hooks/useAuth';

function VendorDashboard() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');
  // We only need the progress state locally to decide whether to trigger
  // the first-run guided tour. If onboarding is complete, no tour.
  const [onboardingComplete, setOnboardingComplete] = useState(true);

  useEffect(() => {
    let cancelled = false;
    vendorApi.listMyOrders()
      .then((data) => { if (!cancelled) setOrders(data || []); })
      .catch((e) => { if (!cancelled) setErr(errorMessage(e)); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  const byStatus = orders.reduce((acc, o) => { acc[o.status] = (acc[o.status] || 0) + 1; return acc; }, {});
  const open = orders.filter((o) => ['ASSIGNED', 'IN_PROGRESS'].includes(o.status));

  return (
    <>
      <PageHeader
        title={`Clinic workspace — ${user?.name || ''}`}
        subtitle="Bookings assigned to your clinic. Manage check-ins, run consultations, and upload patient progress."
        actions={<Link to="/orders" className="btn btn-primary">All bookings</Link>}
      />

      <OnboardingProgress
        onProgress={(p) => setOnboardingComplete(Boolean(p?.isComplete))}
      />

      {/* First-run guided tour — spotlights the "Complete now" CTA on
          the progress banner so the clinic knows exactly what to do
          next. Runs once per browser (persisted in localStorage). */}
      {!onboardingComplete && (
        <OnboardingTour
          storageKey="mdai_vendor_first_tour_v1"
          steps={[
            {
              target: '[data-tour="onboarding-progress"]',
              title: 'Welcome to MyDr.AI!',
              body: "Let's get your clinic ready to receive bookings. This banner tracks your progress through the 5 onboarding steps.",
            },
            {
              target: '[data-tour="onboarding-cta"]',
              title: 'Complete your clinic profile',
              body: 'Click "Complete now" to fill in your MOH/CKAPS, SSM, MMC, APC and other documents. Fields marked with * are required for approval.',
              cta: 'Take me there',
            },
          ]}
        />
      )}

      <div className="grid grid-3">
        <StatCard icon={ClipboardList} tone="brand"   label="Awaiting action" value={open.length} />
        <StatCard icon={Stethoscope}   tone="amber"   label="In treatment"    value={byStatus.IN_PROGRESS || 0} />
        <StatCard icon={FileText}      tone="emerald" label="Completed"       value={byStatus.COMPLETED || 0} />
      </div>

      <div className="card" style={{ marginTop: 'var(--s-4)' }}>
        <div className="card-title">
          <h3>Open work queue</h3>
          <Link to="/orders" className="text-sm">View all <ArrowRight size={14} /></Link>
        </div>

        {loading ? (
          <LoadingSpinner />
        ) : open.length === 0 ? (
          <EmptyState title="Nothing assigned right now" description="When a customer books a treatment with your clinic, it'll appear here." />
        ) : (
          <div className="col">
            {open.slice(0, 8).map((o) => (
              <Link key={o._id} to={`/orders/${o._id}`} className="list-row">
                <div>
                  <div className="bold">{o.orderNumber}</div>
                  <div className="muted text-sm">
                    {o.user?.name || o.user?.email} · {o.package?.name} · booked {formatDate(o.assignedAt || o.createdAt)}
                  </div>
                </div>
                <div className="row">
                  <StatusBadge status={o.status} />
                  <ArrowRight size={16} />
                </div>
              </Link>
            ))}
          </div>
        )}
        {err && <div className="notice warning" style={{ marginTop: 'var(--s-3)' }}>{err}</div>}
      </div>
    </>
  );
}

export default VendorDashboard;
